/**
 * Settings — macOS-style sectioned preferences with permission gates.
 * Shell builtin (app.settings); guests only see personal display panes.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Settings as SettingsGear,
  Monitor,
  Palette,
  Keyboard,
  Shield,
  Download,
  User,
  Package,
  LogOut,
  LayoutGrid,
  Play,
  Pause,
  Wallet,
  RefreshCw,
  Sparkles,
  Check,
  Trash2,
  KeyRound,
} from "lucide-react";
import { osStore } from "../os/store";
import { deviceBus } from "../os/device-host";
import {
  keyRowsFromKeysResponse,
  maskedKeyState,
  keyRowControls,
  keysLaunchIntent,
} from "../os/settings-keys.mjs";
import type { KeyRow } from "../os/settings-keys.mjs";

function useOs() {
  const [, setTick] = useState(0);
  useEffect(() => osStore.subscribe(() => setTick((t) => t + 1)), []);
  return osStore;
}

function userRole(): string {
  try {
    const u = JSON.parse(localStorage.getItem("arclog.user") || "null");
    return String(u?.role || "user").toLowerCase();
  } catch {
    return "user";
  }
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem("arclog.token") || "";
  return token
    ? { Authorization: "Bearer " + token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

type SecId =
  | "general"
  | "display"
  | "wallpaper"
  | "themes"
  | "dock"
  | "keyboard"
  | "account"
  | "packages"
  | "updates"
  | "openrouter"
  | "keys"
  | "security";

type Sec = {
  id: SecId;
  label: string;
  icon: typeof SettingsGear;
  group: string;
  allow: boolean;
};

const SCALES = [
  { v: 0.85, label: "Small (85%)" },
  { v: 0.9, label: "Comfortable (90%)" },
  { v: 1, label: "Default (100%)" },
  { v: 1.1, label: "Large (110%)" },
  { v: 1.25, label: "Extra large (125%)" },
];

const SETTINGS_CSS = `/* Settings - macOS-style sidebar sections */
.settings-shell{display:flex;height:100%;min-height:0;background:rgba(0,0,0,.12);color:var(--os-text,#fff)}
.settings-sidebar{width:210px;min-width:180px;border-right:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03);padding:12px 10px;overflow:auto;display:flex;flex-direction:column;gap:4px}
.settings-sidebar-title{font-size:13px;font-weight:700;letter-spacing:-.02em;padding:4px 8px 10px}
.settings-guest-pill{align-self:flex-start;margin:0 8px 8px;font-size:10px;font-weight:650;padding:3px 8px;border-radius:999px;background:rgba(255,159,10,.18);color:#ffb340}
.settings-nav-group{font-size:10px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;opacity:.4;padding:12px 8px 4px}
.settings-nav-item{width:100%;display:flex;align-items:center;gap:8px;border:none;background:transparent;color:inherit;text-align:left;padding:8px 10px;border-radius:10px;cursor:pointer;font:12.5px var(--os-font-ui,system-ui)}
.settings-nav-item:hover{background:rgba(255,255,255,.07)}
.settings-nav-item.on{background:rgba(10,132,255,.28);font-weight:600}
.settings-main{flex:1;min-width:0;display:flex;flex-direction:column;min-height:0}
.settings-main-h{display:flex;align-items:center;gap:10px;padding:14px 18px;border-bottom:1px solid rgba(255,255,255,.08);font-weight:650}
.settings-main-h h2{margin:0;font-size:15px;font-weight:650;letter-spacing:-.02em}
.settings-main-body{flex:1;overflow:auto;padding:16px 18px 28px;display:flex;flex-direction:column;gap:10px}
.settings-main-body label{display:flex;flex-direction:column;gap:6px;font-size:12px}
.settings-main-body select,.settings-main-body button{border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.06);color:inherit;border-radius:8px;padding:8px 10px;font:12px var(--os-font-ui,system-ui)}
.settings-main-body input{border:1px solid rgba(255,255,255,.14);background:rgba(0,0,0,.28);color:inherit;border-radius:8px;padding:8px 10px;font:12px var(--os-font-ui,system-ui);width:100%;box-sizing:border-box}
.settings-main-body input:focus{outline:none;border-color:var(--os-accent,#0a84ff);box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.25))}
.settings-main-body button{cursor:pointer;display:inline-flex;align-items:center;gap:6px;width:fit-content}
.settings-main-body button:disabled{opacity:.5;cursor:not-allowed}
.settings-main-body button.primary{background:var(--os-accent,#0a84ff);border-color:transparent;color:#fff;font-weight:600}
.settings-main-body button.danger{background:rgba(255,69,58,.18);border-color:rgba(255,69,58,.35)}
.settings-key-row{display:flex;gap:8px;align-items:center}
.settings-key-row input{flex:1}
.settings-key-row button{flex:none}
.settings-key-status{display:flex;align-items:center;gap:8px;font-size:12px;padding:9px 11px;border-radius:9px;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.09)}
.settings-main-body code{font:11px ui-monospace,monospace;background:rgba(255,255,255,.08);padding:1px 5px;border-radius:5px}
.settings-badge{font-size:10px;font-weight:650;padding:2px 8px;border-radius:999px;letter-spacing:.02em;white-space:nowrap}
.settings-badge.ok{background:rgba(48,209,88,.18);color:#4ce07a}
.settings-badge.off{background:rgba(255,159,10,.16);color:#ffb340}
.settings-main-body pre{font:11px/1.45 ui-monospace,monospace;background:rgba(0,0,0,.28);border-radius:10px;padding:10px 12px;overflow:auto;max-height:240px}
.settings-keys-list{display:flex;flex-direction:column;gap:12px}
.settings-key-card{display:flex;flex-direction:column;gap:8px;border:1px solid rgba(255,255,255,.09);border-radius:12px;padding:12px 14px;background:rgba(255,255,255,.03)}
.settings-key-card-h{display:flex;align-items:center;justify-content:space-between;gap:8px;min-width:0}
.settings-key-card-h strong{font-size:12.5px;font-weight:650;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.settings-key-card a{color:var(--os-accent,#0a84ff);font-size:11px}
.settings-key-system{margin-top:2px;padding-top:8px;border-top:1px solid rgba(255,255,255,.08);display:flex;flex-direction:column;gap:6px}
.settings-key-system-label{font-size:10.5px;font-weight:650;letter-spacing:.03em;text-transform:uppercase;opacity:.55}
@media (max-width:720px){.settings-shell{flex-direction:column}.settings-sidebar{width:100%;min-width:0;border-right:none;border-bottom:1px solid rgba(255,255,255,.08);max-height:160px}.settings-nav{display:flex;flex-wrap:wrap;gap:4px}.settings-nav-group{display:none}.settings-nav-item{width:auto}}
`;

function ensureSettingsCss() {
  if (typeof document === "undefined") return;
  if (document.getElementById("arclog-settings-css")) return;
  const s = document.createElement("style");
  s.id = "arclog-settings-css";
  s.textContent = SETTINGS_CSS;
  document.head.appendChild(s);
}

export function SettingsApp({ windowId }: { windowId?: string } = {}) {
  useEffect(() => {
    ensureSettingsCss();
  }, []);
  const store = useOs();
  const wallpapers = store.wallpaperModules();
  const [section, setSection] = useState<SecId>("general");
  type OrSnap = {
    configured: boolean;
    label?: string;
    totalCredits: number | null;
    totalUsage: number | null;
    remainingCredits: number | null;
    keyUsage: number | null;
    keyUsageDaily: number | null;
    keyUsageWeekly: number | null;
    keyUsageMonthly: number | null;
    keyLimit: number | null;
    keyLimitRemaining: number | null;
    keyLimitReset: string | null;
    isFreeTier: boolean | null;
    fetchedAt: string;
    error?: string;
  };
  type OrKeyInfo = {
    configured: boolean;
    source: "user" | "os" | "env" | "file" | "none";
    userConfigured: boolean;
    /** Masked preview of the caller's OWN key only. */
    userMasked: string;
    osConfigured: boolean;
    /** Masked OS key — only populated for admins. */
    osMasked: string;
    canManageOs?: boolean;
  };
  const [orCredits, setOrCredits] = useState<OrSnap | null>(null);
  const [orLoading, setOrLoading] = useState(false);
  const [orError, setOrError] = useState("");
  const [orKey, setOrKey] = useState<OrKeyInfo | null>(null);
  const [orKeyInput, setOrKeyInput] = useState("");
  const [orOsKeyInput, setOrOsKeyInput] = useState("");
  const [orKeySaving, setOrKeySaving] = useState(false);

  const role = userRole();
  const perms = store.permissions || [];

  const isGuest = role === "guest";
  const isAdmin =
    !isGuest &&
    (role === "admin" ||
      perms.includes("os.admin") ||
      perms.includes("os.update.apply") ||
      perms.includes("os.update.check"));
  const canPackages =
    isAdmin || perms.includes("packages.install") || perms.includes("packages.list");
  const canUpdates =
    isAdmin || perms.includes("os.update.check") || perms.includes("os.update.apply");
  // Any signed-in (non-guest) user can manage their own key.
  const canOpenRouter = !isGuest;

  // Keys panel — GET /api/keys returns booleans only (`set`/`systemSet`), never
  // a value, so there is nothing to hold in state beyond that. Every row's
  // input starts empty and is cleared again after every save/clear attempt,
  // success or failure — the typed value never outlives the request.
  const [keyRows, setKeyRows] = useState<KeyRow[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [keysError, setKeysError] = useState("");
  const [keyInputs, setKeyInputs] = useState<Record<string, string>>({});
  const [sysKeyInputs, setSysKeyInputs] = useState<Record<string, string>>({});
  const [keySavingId, setKeySavingId] = useState("");
  const [focusCredential, setFocusCredential] = useState<string | null>(null);
  const keyInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const canKeys = !isGuest;

  async function loadKeys() {
    if (!canKeys) return;
    setKeysLoading(true);
    setKeysError("");
    try {
      const res = await fetch("/api/keys", { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || res.statusText);
      setKeyRows(keyRowsFromKeysResponse(data));
    } catch (e) {
      setKeysError(e instanceof Error ? e.message : String(e));
    } finally {
      setKeysLoading(false);
    }
  }

  // Re-render from the server's answer after every mutation, never from an
  // optimistic assumption — a rejected placeholder returns 400 and the row
  // must still read "Not set" afterward. The input is cleared unconditionally,
  // whether the call succeeded or not.
  async function saveUserKey(id: string) {
    const value = (keyInputs[id] || "").trim();
    if (!value) {
      store.notify("Enter a key first", "error");
      return;
    }
    setKeySavingId(`${id}:user`);
    try {
      const res = await fetch(`/api/keys/${encodeURIComponent(id)}`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ value }),
      });
      const data = await res.json().catch(() => ({}));
      setKeyInputs((m) => ({ ...m, [id]: "" }));
      if (!res.ok) store.notify(data?.error || "could not save key", "error");
      else store.notify("Key saved", "success");
      await loadKeys();
    } catch (e) {
      store.notify(e instanceof Error ? e.message : String(e), "error");
    } finally {
      setKeySavingId("");
    }
  }

  async function clearUserKey(id: string) {
    setKeySavingId(`${id}:user`);
    try {
      await fetch(`/api/keys/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      setKeyInputs((m) => ({ ...m, [id]: "" }));
      store.notify("Key cleared", "success");
      await loadKeys();
    } catch (e) {
      store.notify(e instanceof Error ? e.message : String(e), "error");
    } finally {
      setKeySavingId("");
    }
  }

  async function saveSystemKey(id: string) {
    if (!isAdmin) return;
    const value = (sysKeyInputs[id] || "").trim();
    if (!value) {
      store.notify("Enter a key first", "error");
      return;
    }
    setKeySavingId(`${id}:system`);
    try {
      const res = await fetch(`/api/keys/${encodeURIComponent(id)}/system`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ value }),
      });
      const data = await res.json().catch(() => ({}));
      setSysKeyInputs((m) => ({ ...m, [id]: "" }));
      if (!res.ok) store.notify(data?.error || "could not save OS-wide key", "error");
      else store.notify("OS-wide key saved", "success");
      await loadKeys();
    } catch (e) {
      store.notify(e instanceof Error ? e.message : String(e), "error");
    } finally {
      setKeySavingId("");
    }
  }

  async function loadOrKey() {
    if (!canOpenRouter) return;
    try {
      const res = await fetch("/api/system/openrouter/key", { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setOrKey(data as OrKeyInfo);
    } catch {
      /* leave key state as-is */
    }
  }

  async function loadOpenRouter(refresh = false) {
    if (!canOpenRouter) return;
    setOrLoading(true);
    setOrError("");
    try {
      const q = refresh ? "?refresh=1" : "";
      const res = await fetch("/api/system/openrouter/credits" + q, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setOrCredits(data as OrSnap);
      if (data.key) setOrKey(data.key as OrKeyInfo);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setOrError(msg);
      // Not fatal — a user without their own key can't see OS billing.
    } finally {
      setOrLoading(false);
    }
  }

  async function saveOpenRouterKey(scope: "user" | "os", clear = false) {
    if (scope === "os" && !isAdmin) return;
    const key = clear ? "" : (scope === "os" ? orOsKeyInput : orKeyInput).trim();
    if (!clear && !key) {
      store.notify("Enter an OpenRouter key first", "error");
      return;
    }
    setOrKeySaving(true);
    setOrError("");
    try {
      const res = await fetch("/api/system/openrouter/key", {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ key, scope }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      if (data.key) setOrKey(data.key as OrKeyInfo);
      if (data.credits) setOrCredits(data.credits as OrSnap);
      if (scope === "os") setOrOsKeyInput("");
      else setOrKeyInput("");
      const what = scope === "os" ? "OS-wide key" : "Your OpenRouter key";
      store.notify(clear ? `${what} cleared` : `${what} saved`, "success");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setOrError(msg);
      store.notify(msg, "error");
    } finally {
      setOrKeySaving(false);
    }
  }

  const sections = useMemo(() => {
    const all: Sec[] = [
      { id: "general", label: "General", icon: SettingsGear, group: "System", allow: true },
      { id: "display", label: "Display & Desktop", icon: Monitor, group: "System", allow: true },
      { id: "wallpaper", label: "Wallpaper", icon: Palette, group: "System", allow: true },
      { id: "themes", label: "Themes", icon: Palette, group: "System", allow: true },
      { id: "dock", label: "Dock & Home", icon: LayoutGrid, group: "System", allow: !isGuest },
      { id: "keyboard", label: "Keyboard", icon: Keyboard, group: "System", allow: true },
      { id: "account", label: "Account", icon: User, group: "Account", allow: true },
      {
        id: "packages",
        label: "Packages",
        icon: Package,
        group: "Admin",
        allow: canPackages && !isGuest,
      },
      {
        id: "updates",
        label: "Updates",
        icon: Download,
        group: "Admin",
        allow: canUpdates && !isGuest,
      },
      {
        id: "openrouter",
        label: "OpenRouter (AI)",
        icon: Wallet,
        group: "AI",
        allow: !isGuest,
      },
      {
        id: "keys",
        label: "Keys",
        icon: KeyRound,
        group: "AI",
        allow: !isGuest,
      },
      {
        id: "security",
        label: "Security",
        icon: Shield,
        group: "Admin",
        allow: isAdmin,
      },
    ];
    return all.filter((s) => s.allow);
  }, [isGuest, isAdmin, canPackages, canUpdates, perms]);

  const active = sections.find((s) => s.id === section) || sections[0];
  const activeId = active?.id || "general";
  const ActiveIcon = active?.icon || SettingsGear;

  useEffect(() => {
    if (activeId !== "openrouter" || !canOpenRouter) return;
    void loadOrKey();
    void loadOpenRouter(false);
  }, [activeId, canOpenRouter]);

  useEffect(() => {
    if (activeId !== "keys" || !canKeys) return;
    void loadKeys();
  }, [activeId, canKeys]);

  // ctx.keys.prompt(id) → osStore.openApp("app.settings", …, { panel: "keys",
  // credential: id }). Settings is a singleton, so a SECOND prompt() call
  // re-launches this already-open window rather than remounting it — reading
  // launch params once at mount would honour the first prompt and silently
  // ignore every one after it. Subscribe as well as read, exactly like
  // ctx.launchParams.subscribe does for SDK modules (osStore is the same
  // backing store; this window just isn't going through createModuleContext).
  useEffect(() => {
    if (!windowId) return;
    const applyIntent = (params?: Record<string, unknown>) => {
      const intent = keysLaunchIntent(params);
      if (!intent) return;
      setSection("keys");
      setFocusCredential(intent.credential);
    };
    applyIntent(osStore.launchParamsFor(windowId));
    return osStore.subscribeLaunchParams(windowId, applyIntent);
  }, [windowId]);

  // Scroll to and focus the deep-linked row once it exists in the loaded list
  // (it may not be there yet on the very first render after a prompt() call,
  // while loadKeys() is still in flight).
  useEffect(() => {
    if (activeId !== "keys" || !focusCredential) return;
    const el = keyInputRefs.current[focusCredential];
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.focus();
    }
  }, [activeId, focusCredential, keyRows]);

  useEffect(() => {
    if (!sections.find((s) => s.id === section) && sections[0]) {
      setSection(sections[0].id);
    }
  }, [section, sections]);

  return (
    <div className="settings-shell">
      <aside className="settings-sidebar" aria-label="Settings sections">
        <div className="settings-sidebar-title">Settings</div>
        {isGuest && <div className="settings-guest-pill">Guest · limited</div>}
        <nav className="settings-nav">
          {sections.map((s, i) => {
            const prev = sections[i - 1];
            const Icon = s.icon;
            return (
              <div key={s.id}>
                {(!prev || prev.group !== s.group) && (
                  <div className="settings-nav-group">{s.group}</div>
                )}
                <button
                  type="button"
                  className={"settings-nav-item" + (s.id === activeId ? " on" : "")}
                  onClick={() => setSection(s.id)}
                >
                  <Icon size={15} strokeWidth={2} />
                  <span>{s.label}</span>
                </button>
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="settings-main">
        <div className="settings-main-h">
          <ActiveIcon size={18} strokeWidth={2} />
          <h2>{active?.label || "Settings"}</h2>
        </div>
        <div className="settings-main-body">
          {activeId === "general" && (
            <>
              <p className="settings-hint">
                System preferences. Guest accounts only see personal display options.
              </p>
              <label>
                Shell layout
                <select
                  defaultValue={localStorage.getItem("arclog.shellMode") || "auto"}
                  onChange={(e) => {
                    const v = e.target.value as "auto" | "phone" | "tablet" | "desktop";
                    deviceBus.setModeOverride(v);
                    store.notify("Shell layout · " + v, "info");
                  }}
                >
                  <option value="auto">Auto (by width)</option>
                  <option value="phone">Phone</option>
                  <option value="tablet">Tablet</option>
                  <option value="desktop">Desktop</option>
                </select>
              </label>
              <p className="settings-hint">
                Phone: full-screen apps, home grid, bottom nav. Desktop: floating windows.
              </p>
            </>
          )}

          {activeId === "display" && (
            <>
              <label>
                Display scale
                <select
                  value={String(store.uiScale)}
                  onChange={(e) => store.setUiScale(Number(e.target.value))}
                >
                  {SCALES.map((s) => (
                    <option key={s.v} value={String(s.v)}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <p className="settings-hint">
                Scales the whole UI (icons, chrome, type). Not monitor resolution.
              </p>
              <label>
                Graphics backend
                <select
                  value={store.gfxBackend}
                  onChange={(e) => {
                    store.setGfxBackend(e.target.value);
                    store.notify("Graphics backend saved — reopen apps to apply", "info");
                  }}
                >
                  <option value="three">Three.js (WebGL)</option>
                  <option value="canvas2d">Canvas 2D (lightweight)</option>
                </select>
              </label>
              <p className="settings-hint">
                Apps use ctx.graphics. Three is default; Canvas2D is an alternate driver.
              </p>
            </>
          )}

          {activeId === "wallpaper" && (
            <>
              <label>
                Wallpaper
                <select
                  value={store.wallpaperId}
                  onChange={(e) => store.setWallpaper(e.target.value)}
                >
                  {(wallpapers.length
                    ? wallpapers
                    : [{ id: "wallpaper.aurora", name: "Aurora" } as { id: string; name: string }]
                  ).map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name || w.id}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Animation</span>
                <button
                  type="button"
                  onClick={() => store.setWallpaperPaused(!store.wallpaperPaused)}
                >
                  {store.wallpaperPaused ? (
                    <>
                      <Play size={14} /> Resume wallpaper
                    </>
                  ) : (
                    <>
                      <Pause size={14} /> Pause wallpaper
                    </>
                  )}
                </button>
              </label>
              <p className="settings-hint">
                Open the Wallpaper app for the full gallery (packages + Media images).
              </p>
              <button
                type="button"
                className="primary"
                onClick={() => store.openApp("app.wallpaper", "Wallpaper")}
              >
                Open Wallpaper…
              </button>
            </>
          )}

          {activeId === "themes" && (
            <>
              <p className="settings-hint">
                Themes are portable packages (kind: theme). Install via App Manager or packages API
                the same way as apps and wallpapers. A Windows theme is just another package someone
                can share or publish to an app store later.
              </p>
              <label>
                Active theme
                <select
                  value={store.themeId}
                  onChange={(e) => {
                    const id = e.target.value;
                    void import("../os/theme-host").then(async ({ activateTheme }) => {
                      const vars = await activateTheme(id);
                      store.setTheme(id, vars);
                      store.notify("Theme · " + id, "success");
                    });
                  }}
                >
                  {(store.themeModules().length
                    ? store.themeModules()
                    : [
                        {
                          id: "theme.macos-glass",
                          name: "macOS Glass",
                        } as { id: string; name: string },
                      ]
                  ).map((th) => (
                    <option key={th.id} value={th.id}>
                      {th.name || th.id}
                    </option>
                  ))}
                </select>
              </label>
              <p className="settings-hint">
                Installed:{" "}
                {store.themeModules().map((m) => m.id).join(", ") || "theme.macos-glass"}
              </p>
            </>
          )}

          {activeId === "dock" && (
            <>
              <p className="settings-hint">
                Dock shows pins only. Catalog: Files → Applications, App Manager, or
                Ctrl+Alt+Space.
              </p>
              <strong style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Package size={14} /> Dock pins
              </strong>
              <pre>{store.dockPins.join("\n") || "(none)"}</pre>
              <p className="settings-hint">
                Right-click a dock app → Pin / Unpin / Add to Home.
              </p>
            </>
          )}

          {activeId === "keyboard" && (
            <ul className="settings-keys">
              <li>
                <kbd>Ctrl</kbd>+<kbd>Alt</kbd>+<kbd>Space</kbd> App launcher
              </li>
              <li>
                <kbd>Ctrl</kbd>+<kbd>Alt</kbd>+<kbd>F</kbd> Files
              </li>
              <li>
                <kbd>Ctrl</kbd>+<kbd>Alt</kbd>+<kbd>A</kbd> Applications
              </li>
            </ul>
          )}

          {activeId === "account" && (
            <>
              <p className="settings-hint">
                Signed in as <strong>{role}</strong>
                {perms.length ? ` · ${perms.length} permissions` : ""}.
              </p>
              {isGuest && (
                <p className="settings-hint">
                  Guest cannot install packages, open Studio/Terminal, or apply OS updates.
                </p>
              )}
              <button
                type="button"
                className="danger"
                onClick={() => {
                  store.setToken(null);
                  location.reload();
                }}
              >
                <LogOut size={14} /> Log out
              </button>
            </>
          )}

          {activeId === "packages" && (
            <>
              <p className="settings-hint">Installed packages visible to this session.</p>
              <pre>
                {store.packages
                  .map(
                    (p) =>
                      `${p.manifest.id} [${p.state}] v${p.defaultVersion || p.manifest.version}`
                  )
                  .join("\n")}
              </pre>
              <button type="button" onClick={() => store.openApp("app.packages", "App Manager")}>
                Open App Manager…
              </button>
            </>
          )}

          {activeId === "updates" && (
            <div className="settings-updates">
              <p className="settings-hint">
                Updates notify only. Install is always explicit and takes a snapshot first.
              </p>
              <div className="settings-update-actions">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const res = await fetch("/api/os/update/check", {
                        headers: authHeaders(),
                      });
                      const data = await res.json();
                      store.notify(
                        res.ok
                          ? "Checked catalog · " +
                              ((data.updates && data.updates.length) || 0) +
                              " available"
                          : data.error || "check failed",
                        res.ok ? "info" : "error"
                      );
                    } catch (e) {
                      store.notify(e instanceof Error ? e.message : String(e), "error");
                    }
                  }}
                >
                  Check for updates
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const res = await fetch("/api/os/update/snapshots", {
                        method: "POST",
                        headers: authHeaders(),
                        body: JSON.stringify({ label: "Manual snapshot" }),
                      });
                      const data = await res.json();
                      if (!res.ok) throw new Error(data.error || res.statusText);
                      store.notify("Snapshot " + data.id, "success");
                    } catch (e) {
                      store.notify(e instanceof Error ? e.message : String(e), "error");
                    }
                  }}
                >
                  Create snapshot
                </button>
              </div>
            </div>
          )}


          {activeId === "openrouter" && (
            <>
              <p className="settings-hint">
                OpenRouter powers AI in Studio &amp; Hermes. Use <strong>your own key</strong>
                {" "}(billed to you); if you don&apos;t set one, the OS-wide fallback is used.
                Keys are stored on the server and never sent back to the browser.
              </p>

              <div className="settings-key-status">
                <Sparkles size={15} />
                {orKey?.userConfigured ? (
                  <span>
                    Using <strong>your key</strong>{" "}
                    <span className="settings-badge ok">Personal</span> · <code>{orKey.userMasked}</code>
                  </span>
                ) : orKey?.osConfigured ? (
                  <span>
                    Using the <strong>OS-wide key</strong>{" "}
                    <span className="settings-badge off">Fallback</span> · set your own below to bill
                    separately.
                  </span>
                ) : (
                  <span>
                    No key <span className="settings-badge off">Not set</span> · Studio &amp; Hermes
                    are disabled until you or an admin add one.
                  </span>
                )}
              </div>

              {/* Personal key — any signed-in user */}
              <label>
                {orKey?.userConfigured ? "Update your API key" : "Set your API key"}
                <div className="settings-key-row">
                  <input
                    type="password"
                    value={orKeyInput}
                    onChange={(e) => setOrKeyInput(e.target.value)}
                    placeholder="sk-or-…"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    className="primary"
                    disabled={orKeySaving || !orKeyInput.trim()}
                    onClick={() => void saveOpenRouterKey("user", false)}
                  >
                    <Check size={14} /> {orKeySaving ? "Saving…" : "Save"}
                  </button>
                </div>
              </label>
              <p className="settings-hint">
                Used for your Studio &amp; Hermes calls and billed to your OpenRouter account.
              </p>
              {orKey?.userConfigured && (
                <button
                  type="button"
                  className="danger"
                  disabled={orKeySaving}
                  onClick={() => void saveOpenRouterKey("user", true)}
                >
                  <Trash2 size={14} /> Clear my key (use OS fallback)
                </button>
              )}

              {/* OS-wide fallback — admins only */}
              {isAdmin && (
                <>
                  <div
                    style={{
                      marginTop: 6,
                      paddingTop: 12,
                      borderTop: "1px solid rgba(255,255,255,.1)",
                      fontSize: 12,
                      fontWeight: 650,
                      opacity: 0.75,
                    }}
                  >
                    OS-wide fallback key (admin)
                  </div>
                  <p className="settings-hint">
                    Used by anyone who hasn&apos;t set their own key, and set at install.{" "}
                    {orKey?.osConfigured ? (
                      <>
                        Current: <code>{orKey.osMasked || "set"}</code>.
                      </>
                    ) : (
                      "Not set."
                    )}
                  </p>
                  <label>
                    {orKey?.osConfigured ? "Update OS-wide key" : "Set OS-wide key"}
                    <div className="settings-key-row">
                      <input
                        type="password"
                        value={orOsKeyInput}
                        onChange={(e) => setOrOsKeyInput(e.target.value)}
                        placeholder="sk-or-…"
                        autoComplete="off"
                        spellCheck={false}
                      />
                      <button
                        type="button"
                        className="primary"
                        disabled={orKeySaving || !orOsKeyInput.trim()}
                        onClick={() => void saveOpenRouterKey("os", false)}
                      >
                        <Check size={14} /> Save
                      </button>
                    </div>
                  </label>
                  {orKey?.osConfigured && (
                    <button
                      type="button"
                      className="danger"
                      disabled={orKeySaving}
                      onClick={() => void saveOpenRouterKey("os", true)}
                    >
                      <Trash2 size={14} /> Clear OS-wide key
                    </button>
                  )}
                </>
              )}

              <div className="settings-update-actions">
                <button
                  type="button"
                  className="primary"
                  disabled={orLoading}
                  onClick={() => void loadOpenRouter(true)}
                >
                  <RefreshCw size={14} /> {orLoading ? "Loading..." : "Refresh credits"}
                </button>
              </div>
              {orError ? (
                <p className="settings-hint" style={{ color: "var(--os-danger, #ff453a)" }}>
                  {orError}
                </p>
              ) : null}
              {orCredits ? (
                <>
                  <label>
                    Remaining credits
                    <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.03em" }}>
                      {orCredits.remainingCredits == null
                        ? "-"
                        : orCredits.remainingCredits.toLocaleString(undefined, {
                            maximumFractionDigits: 4,
                          })}
                    </div>
                  </label>
                  <pre>
                    {JSON.stringify(
                      {
                        configured: orCredits.configured,
                        remainingCredits: orCredits.remainingCredits,
                        totalCredits: orCredits.totalCredits,
                        totalUsage: orCredits.totalUsage,
                        keyUsage: orCredits.keyUsage,
                        keyUsageDaily: orCredits.keyUsageDaily,
                        keyUsageWeekly: orCredits.keyUsageWeekly,
                        keyUsageMonthly: orCredits.keyUsageMonthly,
                        keyLimit: orCredits.keyLimit,
                        keyLimitRemaining: orCredits.keyLimitRemaining,
                        keyLimitReset: orCredits.keyLimitReset,
                        isFreeTier: orCredits.isFreeTier,
                        label: orCredits.label,
                        fetchedAt: orCredits.fetchedAt,
                        error: orCredits.error,
                      },
                      null,
                      2
                    )}
                  </pre>
                  <p className="settings-hint">
                    remaining = total_credits - total_usage (account). Key usage is spend on this API key.
                  </p>
                </>
              ) : (
                !orLoading && (
                  <p className="settings-hint">Loading credits...</p>
                )
              )}
            </>
          )}

          {activeId === "keys" && (
            <>
              <p className="settings-hint">
                Every credential an installed app can use, in one place. Keys are stored on the
                server and never sent back to the browser — this panel can tell you whether one
                is set, never what it is.
              </p>
              {keysLoading && !keyRows.length && (
                <p className="settings-hint">Loading keys...</p>
              )}
              {keysError && (
                <p className="settings-hint" style={{ color: "var(--os-danger, #ff453a)" }}>
                  {keysError}
                </p>
              )}
              {!keysLoading && !keysError && !keyRows.length && (
                <p className="settings-hint">No credentials to manage yet.</p>
              )}
              <div className="settings-keys-list">
                {keyRows.map((row) => {
                  const controls = keyRowControls(row, isAdmin);
                  const savingUser = keySavingId === `${row.id}:user`;
                  const savingSystem = keySavingId === `${row.id}:system`;
                  return (
                    <div className="settings-key-card" key={row.id}>
                      <div className="settings-key-card-h">
                        <strong>{row.label}</strong>
                        <span className={"settings-badge " + (row.set ? "ok" : "off")}>
                          {maskedKeyState(row)}
                        </span>
                      </div>
                      {row.help && (
                        row.help.startsWith("http") ? (
                          <a href={row.help} target="_blank" rel="noreferrer">
                            {row.help}
                          </a>
                        ) : (
                          <p className="settings-hint">{row.help}</p>
                        )
                      )}
                      {row.kind === "password" ? (
                        <div className="settings-key-row" style={{ alignItems: "center", gap: 10 }}>
                          <span className="settings-hint" style={{ flex: 1 }}>
                            Username + password login — managed in KeyChain.
                          </span>
                          <button
                            type="button"
                            className="primary"
                            onClick={() => store.openApp("app.keychain", "KeyChain", undefined, { credential: row.id })}
                          >
                            <KeyRound size={14} /> Open KeyChain
                          </button>
                        </div>
                      ) : (
                      <div className="settings-key-row">
                        <input
                          ref={(el) => {
                            keyInputRefs.current[row.id] = el;
                          }}
                          type="password"
                          value={keyInputs[row.id] || ""}
                          onChange={(e) =>
                            setKeyInputs((m) => ({ ...m, [row.id]: e.target.value }))
                          }
                          placeholder={row.set ? "Replace your key…" : "Enter your key…"}
                          autoComplete="off"
                          spellCheck={false}
                        />
                        <button
                          type="button"
                          className="primary"
                          disabled={savingUser || !(keyInputs[row.id] || "").trim()}
                          onClick={() => void saveUserKey(row.id)}
                        >
                          <Check size={14} /> {savingUser ? "Saving…" : "Save"}
                        </button>
                        {row.set && (
                          <button
                            type="button"
                            className="danger"
                            disabled={savingUser}
                            onClick={() => void clearUserKey(row.id)}
                          >
                            <Trash2 size={14} /> Clear
                          </button>
                        )}
                      </div>
                      )}

                      {controls.showUserOnlyNote && (
                        <p className="settings-hint">This app uses your own key only.</p>
                      )}

                      {controls.showSystemControl && row.kind !== "password" && (
                        <div className="settings-key-system">
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span className="settings-key-system-label">OS-wide key (admin)</span>
                            <span className={"settings-badge " + (row.systemSet ? "ok" : "off")}>
                              {row.systemSet ? "Set" : "Not set"}
                            </span>
                          </div>
                          <div className="settings-key-row">
                            <input
                              type="password"
                              value={sysKeyInputs[row.id] || ""}
                              onChange={(e) =>
                                setSysKeyInputs((m) => ({ ...m, [row.id]: e.target.value }))
                              }
                              placeholder={
                                row.systemSet ? "Replace the OS-wide key…" : "Set the OS-wide key…"
                              }
                              autoComplete="off"
                              spellCheck={false}
                            />
                            <button
                              type="button"
                              className="primary"
                              disabled={savingSystem || !(sysKeyInputs[row.id] || "").trim()}
                              onClick={() => void saveSystemKey(row.id)}
                            >
                              <Check size={14} /> {savingSystem ? "Saving…" : "Save"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {activeId === "security" && (
            <>
              <p className="settings-hint">
                Admin-only. Roles and permissions are enforced server-side.
              </p>
              <pre>
                {`role: ${role}\npermissions:\n${
                  perms.length ? perms.join("\n") : "(none listed)"
                }`}
              </pre>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
